<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-100">
    <div class="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">

      <!-- Verifying link on mount -->
      <template v-if="step === 'initializing'">
        <h2 class="text-2xl font-bold text-center text-gray-700">Verifying Link</h2>
        <p class="text-center text-gray-500">Please wait while we verify your reset link…</p>
        <div class="flex justify-center mt-6">
          <svg class="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
        </div>
      </template>

      <!-- 2FA TOTP input -->
      <template v-else-if="step === 'totp'">
        <h2 class="text-2xl font-bold text-center text-gray-700">Verify Your Identity</h2>
        <p class="text-center text-gray-500">
          Enter the 6-digit code from your authenticator app to continue.
        </p>
        <form @submit.prevent="handleTotpSubmit">
          <div>
            <label for="totp" class="block text-sm font-medium text-gray-700">Verification Code</label>
            <input
              v-model="totpCode"
              id="totp"
              name="totp"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{6}"
              maxlength="6"
              autocomplete="one-time-code"
              required
              class="w-full px-3 py-2 mt-1 text-center text-lg font-mono border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="000000"
              @input="handleTotpInput"
            />
            <p class="mt-1 text-xs text-gray-400">Codes refresh every 30 seconds.</p>
          </div>
          <div class="mt-6">
            <button
              type="submit"
              :disabled="isLoading || totpCode.length !== 6"
              class="btn-primary w-full flex justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isLoading">Verifying…</span>
              <span v-else>Verify</span>
            </button>
          </div>
        </form>
        <div v-if="errorMessage" class="p-4 mt-2 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
          {{ errorMessage }}
        </div>
        <div class="text-sm text-center">
          <router-link to="/forgot-password" class="font-medium text-primary-600 hover:text-primary-500">
            Request a new link
          </router-link>
        </div>
      </template>

      <!-- New password form -->
      <template v-else-if="step === 'password'">
        <h2 class="text-2xl font-bold text-center text-gray-700">Reset Your Password</h2>
        <p class="text-center text-gray-500">Enter a new, secure password below.</p>
        <form @submit.prevent="handleResetPassword">
          <div class="space-y-4">
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700">New Password</label>
              <div class="relative">
                <input
                  v-model="newPassword"
                  id="password"
                  name="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  minlength="8"
                  class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter new password"
                  @input="checkPasswordStrength"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                  </svg>
                </button>
              </div>
              <div v-if="newPassword" class="mt-2 flex items-center space-x-2">
                <div class="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    class="h-2 rounded-full transition-all duration-300"
                    :class="passwordStrengthClass"
                    :style="{ width: passwordStrength + '%' }"
                  ></div>
                </div>
                <span class="text-sm" :class="passwordStrengthTextClass">{{ passwordStrengthText }}</span>
              </div>
            </div>

            <div>
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700">Confirm Password</label>
              <div class="relative">
                <input
                  v-model="confirmPassword"
                  id="confirmPassword"
                  name="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg v-if="!showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div class="mt-2 text-sm text-gray-600">
            <p>Password requirements:</p>
            <ul class="mt-1 ml-4 list-disc">
              <li>At least 8 characters long</li>
              <li>Include uppercase and lowercase letters</li>
              <li>Include at least one number</li>
              <li>Include at least one special character</li>
            </ul>
          </div>

          <div class="mt-6">
            <button
              type="submit"
              :disabled="isLoading || !isPasswordValid"
              class="btn-primary w-full flex justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isLoading">Resetting Password…</span>
              <span v-else>Reset Password</span>
            </button>
          </div>
        </form>

        <div v-if="errorMessage" class="p-4 mt-2 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
          {{ errorMessage }}
        </div>

        <div class="text-sm text-center">
          <router-link to="/login" class="font-medium text-primary-600 hover:text-primary-500">
            Back to Login
          </router-link>
        </div>
      </template>

      <!-- Success -->
      <template v-else-if="step === 'success'">
        <div class="text-center space-y-4">
          <div class="flex justify-center">
            <svg class="h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-700">Password Reset</h2>
          <p class="text-gray-500">Your password has been reset. You can now log in with your new password.</p>
          <p class="text-sm text-gray-400">Redirecting to login…</p>
          <router-link to="/login" class="inline-block font-medium text-primary-600 hover:text-primary-500">
            Go to Login
          </router-link>
        </div>
      </template>

      <!-- Expired / invalid link -->
      <template v-else-if="step === 'error'">
        <div class="text-center space-y-4">
          <div class="flex justify-center">
            <svg class="h-16 w-16 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-700">Link Expired or Invalid</h2>
          <p class="text-gray-500">{{ errorMessage }}</p>
          <router-link
            to="/forgot-password"
            class="inline-block font-medium text-primary-600 hover:text-primary-500"
          >
            Request a New Link
          </router-link>
        </div>
      </template>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'

type Step = 'initializing' | 'totp' | 'password' | 'success' | 'error'

const step = ref<Step>('initializing')
const errorMessage = ref<string | null>(null)
const isLoading = ref(false)

// URL-sourced state
const pageEmail = ref('')
const pageResetToken = ref('')

// TOTP step state
const totpCode = ref('')

// Password step state
const newPassword = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const passwordStrength = ref(0)

// Kept only in component memory — never persisted (5 min TTL)
const temporaryToken = ref<string | null>(null)

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

onMounted(async () => {
  const token = route.query.token as string | undefined
  const email = route.query.email as string | undefined

  if (!token || !email) {
    router.replace('/forgot-password')
    return
  }

  pageEmail.value = decodeURIComponent(email)
  pageResetToken.value = token

  await attemptVerify()
})

const attemptVerify = async (totp?: string) => {
  isLoading.value = true
  errorMessage.value = null

  try {
    const result = await authStore.verifyPasswordReset(pageEmail.value, pageResetToken.value, totp)
    temporaryToken.value = result.temporary_token
    step.value = 'password'
  } catch (err: any) {
    if (err?.status === 400) {
      // Server indicates 2FA is required
      step.value = 'totp'
    } else if (err?.status === 401) {
      step.value = 'error'
      errorMessage.value = 'This link has expired or is invalid. Please request a new one.'
    } else {
      step.value = 'error'
      errorMessage.value = 'Something went wrong. Please try again.'
    }
  } finally {
    isLoading.value = false
  }
}

const handleTotpInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  input.value = input.value.replace(/[^0-9]/g, '')
  totpCode.value = input.value
  if (totpCode.value.length === 6) {
    handleTotpSubmit()
  }
}

const handleTotpSubmit = async () => {
  if (totpCode.value.length !== 6 || isLoading.value) return
  errorMessage.value = null

  try {
    isLoading.value = true
    const result = await authStore.verifyPasswordReset(pageEmail.value, pageResetToken.value, totpCode.value)
    temporaryToken.value = result.temporary_token
    step.value = 'password'
  } catch (err: any) {
    if (err?.status === 401) {
      errorMessage.value = 'Incorrect code. Please try again.'
    } else {
      errorMessage.value = 'Something went wrong. Please try again.'
    }
    totpCode.value = ''
  } finally {
    isLoading.value = false
  }
}

const checkPasswordStrength = () => {
  let strength = 0
  const pwd = newPassword.value
  if (pwd.length >= 8) strength += 25
  if (pwd.length >= 12) strength += 10
  if (/[A-Z]/.test(pwd)) strength += 20
  if (/[a-z]/.test(pwd)) strength += 20
  if (/[0-9]/.test(pwd)) strength += 20
  if (/[^A-Za-z0-9]/.test(pwd)) strength += 15
  passwordStrength.value = Math.min(strength, 100)
}

const passwordStrengthClass = computed(() => {
  if (passwordStrength.value < 40) return 'bg-red-500'
  if (passwordStrength.value < 60) return 'bg-yellow-500'
  if (passwordStrength.value < 80) return 'bg-blue-500'
  return 'bg-green-500'
})

const passwordStrengthText = computed(() => {
  if (passwordStrength.value < 40) return 'Weak'
  if (passwordStrength.value < 60) return 'Fair'
  if (passwordStrength.value < 80) return 'Good'
  return 'Strong'
})

const passwordStrengthTextClass = computed(() => {
  if (passwordStrength.value < 40) return 'text-red-600'
  if (passwordStrength.value < 60) return 'text-yellow-600'
  if (passwordStrength.value < 80) return 'text-blue-600'
  return 'text-green-600'
})

const isPasswordValid = computed(() =>
  newPassword.value.length >= 8 &&
  newPassword.value === confirmPassword.value &&
  /[A-Z]/.test(newPassword.value) &&
  /[a-z]/.test(newPassword.value) &&
  /[0-9]/.test(newPassword.value) &&
  /[^A-Za-z0-9]/.test(newPassword.value)
)

const handleResetPassword = async () => {
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'Passwords do not match.'
    return
  }
  if (!isPasswordValid.value) {
    errorMessage.value = 'Password does not meet all requirements.'
    return
  }
  if (!temporaryToken.value) {
    router.replace('/forgot-password')
    return
  }

  isLoading.value = true
  errorMessage.value = null

  try {
    await authStore.completePasswordReset(pageEmail.value, temporaryToken.value, newPassword.value)
    step.value = 'success'
    setTimeout(() => router.push('/login'), 3000)
  } catch (err: any) {
    if (err?.status === 401) {
      // Temporary token expired — send back to start
      router.replace({ path: '/forgot-password', query: { reason: 'session-expired' } })
    } else if (err?.status === 400) {
      errorMessage.value = err.message || 'Please check your input and try again.'
    } else {
      errorMessage.value = 'Something went wrong. Please try again.'
    }
  } finally {
    isLoading.value = false
  }
}
</script>
